import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';

export interface QuotaLevel {
  used: number;
  limit: number | null;
}

/** Deduplication savings aggregate returned by GET /quota. */
export interface DedupSavings {
  /** Sum of every media record's logical fileSize (pre-dedup footprint). */
  logicalBytes: number;
  /** Sum of unique content-object sizes (each stored once). */
  physicalBytes: number;
  /** max(0, logicalBytes - physicalBytes). */
  savedBytes: number;
  /** Content objects referenced by more than one media record. */
  sharedObjects: number;
  /** Total number of content objects. */
  contentObjects: number;
}

export interface QuotaState {
  user: QuotaLevel | null;
  system: QuotaLevel | null;
  loading: boolean;
  error: string | null;
}

const initialState: QuotaState = {
  user: null,
  system: null,
  loading: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Fetch quota status from GET /quota.
 * Returns user and system quota levels.
 * Requirements: 26.5
 */
export const fetchQuota = createAsyncThunk(
  'quota/fetchQuota',
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<{
        user: QuotaLevel;
        system: QuotaLevel;
        dedup?: DedupSavings;
      }>('/quota');
      return response.data;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load quota';
      return rejectWithValue(message);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const quotaSlice = createSlice({
  name: 'quota',
  initialState,
  reducers: {
    setQuota(
      state,
      action: PayloadAction<{
        user: QuotaLevel;
        system: QuotaLevel;
      }>,
    ) {
      state.user = action.payload.user;
      state.system = action.payload.system;
      state.loading = false;
      state.error = null;
    },
    setQuotaLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setQuotaError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuota.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuota.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.system = action.payload.system;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchQuota.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? 'Failed to load quota';
      });
  },
});

export const { setQuota, setQuotaLoading, setQuotaError } = quotaSlice.actions;

// Selectors
export const selectUserQuota = (state: { quota: QuotaState }) => state.quota.user;
export const selectSystemQuota = (state: { quota: QuotaState }) => state.quota.system;
export const selectQuotaLoading = (state: { quota: QuotaState }) => state.quota.loading;
export const selectQuotaError = (state: { quota: QuotaState }) => state.quota.error;

export default quotaSlice.reducer;
